import { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router";
import { useUser, UserButton } from "@clerk/clerk-react";
import { keepPreviousData, useMutation, useQuery } from "@tanstack/react-query";
import {
  Building2,
  ChevronLeft,
  ChevronRight,
  ExternalLink,
  Eye,
  Globe,
  Layers,
  Linkedin,
  Loader2,
  Mail,
  MapPin,
  Megaphone,
  Plus,
  Search,
  Twitter,
  X,
} from "lucide-react";
import dgptLogo from "@/assets/dgptlogo.png";
import { toast } from "sonner";
import {
  raiseBoardApi,
  type RaiseBoardBySlugResponse,
} from "@/api/raise-board-api";
import { axiosInstance } from "@/lib/axios-instance";
import { useWorkspaceStore } from "@/store/workspace-store";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Dialog,
  DialogContent,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { getErrorMessage } from "@/lib/api-error";
import { getStageText, GICSIndustries, GICSSubIndustries } from "@/lib/utils";
import { MultiSelect } from "@/components/ui/multi-select";
import { buildFilterOptions } from "./board-filter-options";
import { useDebouncedValue } from "@/hooks/use-debounced-value";
import { CompanyAvatar } from "./company-avatar";
import { BoardAuthDialog } from "@/components/auth/board-auth-dialog";
import { SubscribeButton } from "./subscribe-button";

type Application =
  RaiseBoardBySlugResponse["result"]["applications"][number];

/**
 * Rows per page. Boards range from a handful of deals to the YC Alumni board's
 * ~6,200 — at which point the unpaginated payload was ~8.5MB, so the page asks
 * the API for a slice rather than filtering the whole board in the browser.
 */
const PAGE_SIZE = 25;


function getCompany(application: Application) {
  const ac = application.applicant_company;
  if (!ac || typeof ac === "string") return null;
  return ac;
}

// Build a readable industries string from the deal's GICS sub-industry codes,
// resolved to names here on the FE since the GICS taxonomy lives client-side;
// falls back to the plain industry array.
function resolveIndustries(application: Application): string {
  const company = getCompany(application);
  const subIndustriesField = application.deal_info?.find(
    (f) => f.field_name === "sub_industries" && f.field_state === "approved"
  );
  if (subIndustriesField?.field_value) {
    const codes = Array.isArray(subIndustriesField.field_value)
      ? subIndustriesField.field_value
      : [subIndustriesField.field_value as string];
    const names = codes
      .map((c) => GICSSubIndustries.find((si) => si.code === String(c))?.name)
      .filter((n): n is string => !!n);
    if (names.length > 0) return names.join(", ");
  }
  const fallback = application.industry ?? company?.industry ?? [];
  return fallback.filter(Boolean).join(", ");
}

function getRowDetails(application: Application) {
  const company = getCompany(application);
  const stageValue = application.stage ?? company?.stage;
  const stageLabel = stageValue != null ? getStageText(stageValue) : null;

  let industry: string | undefined;
  const dealInfo = application.deal_info;
  if (dealInfo) {
    const subIndustriesField = dealInfo.find(
      (f) => f.field_name === "sub_industries" && f.field_state === "approved"
    );
    if (subIndustriesField?.field_value) {
      const codes = Array.isArray(subIndustriesField.field_value)
        ? subIndustriesField.field_value
        : [subIndustriesField.field_value as string];
      const name = codes
        .map((c) => GICSSubIndustries.find((si) => si.code === String(c))?.name)
        .find((n): n is string => !!n);
      if (name) industry = name;
    }
  }
  if (!industry) {
    industry = (application.industry ?? company?.industry)?.[0];
  }

  const bio = company?.short_bio ?? application.short_bio;
  const dealInfoCountry = dealInfo?.find(
    (f) => f.field_name === "address"
  )?.field_value as string | undefined;
  const country = dealInfoCountry || company?.country || application.country;

  return { company, stageLabel, industry, bio, country };
}

export default function RaiseBoardPublic() {
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();
  const { isLoaded, isSignedIn, user } = useUser();
  const { activeWorkspaceId } = useWorkspaceStore();
  const [pendingAdd, setPendingAdd] = useState<{ id: string; name: string } | null>(null);
  const [viewing, setViewing] = useState<Application | null>(null);
  const [emailing, setEmailing] = useState<Application | null>(null);
  const [authReason, setAuthReason] = useState<string | null>(null);

  const [search, setSearch] = useState("");
  const [stageFilter, setStageFilter] = useState<string[]>([]);
  const [industryFilter, setIndustryFilter] = useState<string[]>([]);
  const [countryFilter, setCountryFilter] = useState<string[]>([]);
  const [page, setPage] = useState(1);

  // Search trails the input so typing doesn't fire a request per keystroke.
  const debouncedSearch = useDebouncedValue(search, 300);

  // Any change to what's being asked for puts you back on page one; staying on
  // page 7 of a result set that just shrank to two pages shows nothing.
  useEffect(() => {
    setPage(1);
  }, [debouncedSearch, stageFilter, industryFilter, countryFilter]);

  const { data, isLoading, isFetching, error } = useQuery({
    queryKey: [
      "raise-board",
      slug,
      { page, debouncedSearch, stageFilter, industryFilter, countryFilter },
    ],
    queryFn: () => {
      if (!slug) throw new Error("Missing slug");
      return raiseBoardApi.getBySlug(slug, {
        page,
        limit: PAGE_SIZE,
        search: debouncedSearch,
        stage: stageFilter,
        industry: industryFilter,
        country: countryFilter,
      });
    },
    enabled: !!slug,
    retry: false,
    // Hold the previous page on screen while the next one loads, so paging
    // doesn't collapse the layout to skeletons on every click.
    placeholderData: keepPreviousData,
  });

  useEffect(() => {
    document.body.classList.add("public-route");
    return () => document.body.classList.remove("public-route");
  }, []);

  // Computed here rather than after the loading guards because hooks can't sit
  // below an early return, and held on the previous value while a page loads so
  // the dropdowns don't flicker empty mid-browse.
  const filterOptions = useMemo(
    () => buildFilterOptions(data?.result?.facets),
    [data]
  );

  const addToPipeline = useMutation({
    mutationFn: async (companyId: string) => {
      if (!isLoaded) throw new Error("Auth still loading");
      if (!isSignedIn || !user?.id) {
        setAuthReason("Sign in to add this deal to your pipeline.");
        throw new Error("redirect-to-trial");
      }
      if (!activeWorkspaceId) {
        throw new Error(
          "Select a workspace in the main app before adding deals here."
        );
      }
      const { data: response } = await axiosInstance.post(
        "/create_opportunity_application_explore",
        {
          applicant_company: companyId,
          pertains_to_opportunity: activeWorkspaceId,
          changed_by: user.id,
          source: "RaiseBoard",
        }
      );
      return response;
    },
    // The deal-detail route resolves its :id as the applicant COMPANY id
    // (fetch_single_deal queries by company_id), not the application id the
    // create endpoint returns — opening result._id lands on "Deal not found".
    onSuccess: (_response, companyId) => {
      toast.success("Added to your pipeline.");
      if (activeWorkspaceId && companyId) {
        window.open(
          `/${activeWorkspaceId}/deals/${companyId}`,
          "_blank",
          "noopener,noreferrer"
        );
      }
    },
    onError: (e: unknown) => {
      if ((e as Error)?.message === "redirect-to-trial") return;
      toast.error(getErrorMessage(e, "Failed to add to your pipeline"));
    },
  });

  const sendEmail = useMutation({
    mutationFn: async (vars: { application: Application; email: string }) => {
      if (!slug) throw new Error("Missing slug");
      return raiseBoardApi.sendDealEmail({
        slug,
        applicationId: String(vars.application._id),
        recipientEmail: vars.email,
        industries: resolveIndustries(vars.application),
      });
    },
    onSuccess: (_data, vars) => {
      toast.success(`Deal details sent to ${vars.email}.`);
      setEmailing(null);
    },
    onError: (e: unknown) => {
      toast.error(getErrorMessage(e, "Failed to send deal details"));
    },
  });

  if (!slug) {
    return (
      <div className="flex min-h-screen items-center justify-center text-sm text-muted-foreground">
        Missing slug.
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="h-dvh overflow-y-auto bg-background">
        {/* Header strip — same chrome as loaded state */}
        <div className="border-b border-border bg-card">
          <div className="mx-auto flex h-14 max-w-5xl items-center justify-between gap-4 px-4 sm:px-6">
            <img src={dgptLogo} alt="DiligenceGPT" className="h-7 w-auto" />
            {isLoaded && isSignedIn ? (
              <div className="flex items-center gap-3">
                <Skeleton className="hidden h-4 w-32 sm:block" />
                <Skeleton className="h-8 w-8 rounded-full" />
              </div>
            ) : (
              <div className="flex items-center gap-2">
                <Skeleton className="hidden h-7 w-16 sm:block" />
                <Skeleton className="h-9 w-20" />
              </div>
            )}
          </div>
        </div>

        {/* Title section skeleton */}
        <header className="border-b border-border bg-card">
          <div className="mx-auto flex max-w-5xl flex-col gap-2 px-4 py-6 sm:flex-row sm:items-end sm:justify-between sm:px-6 sm:py-8">
            <div>
              <p className="text-xs font-medium uppercase tracking-widest text-muted-foreground">
                Raiseboard
              </p>
              <Skeleton className="mt-2 h-8 w-64" />
            </div>
            <Skeleton className="h-4 w-40" />
          </div>
        </header>

        {/* Table skeleton */}
        <main className="mx-auto max-w-5xl px-4 py-6 sm:px-6 sm:py-8">
          <div className="space-y-3 md:hidden">
            {[0, 1, 2].map((i) => (
              <div key={i} className="rounded-lg border border-border bg-card p-4">
                <div className="flex items-start gap-3">
                  <Skeleton className="h-10 w-10 rounded-lg" />
                  <div className="min-w-0 flex-1 space-y-1.5">
                    <Skeleton className="h-4 w-40" />
                    <Skeleton className="h-3 w-full" />
                  </div>
                </div>
                <div className="mt-3 flex items-center gap-3">
                  <Skeleton className="h-5 w-16 rounded-full" />
                  <Skeleton className="h-4 w-24" />
                </div>
                <div className="mt-3 flex gap-2">
                  <Skeleton className="h-8 flex-1" />
                  <Skeleton className="h-8 flex-1" />
                </div>
              </div>
            ))}
          </div>
          <div className="hidden overflow-hidden rounded-lg border border-border md:block">
            <div className="grid grid-cols-[1fr_100px_160px_140px_44px_44px] items-center gap-4 border-b border-border bg-muted/40 px-4 py-3">
              <Skeleton className="h-3 w-20" />
              <Skeleton className="h-3 w-12" />
              <Skeleton className="h-3 w-16" />
              <Skeleton className="h-3 w-16" />
              <span className="col-span-2 flex justify-center">
                <Skeleton className="h-3 w-14" />
              </span>
            </div>
            {[0, 1, 2].map((i) => (
              <div
                key={i}
                className="grid grid-cols-[1fr_100px_160px_140px_44px_44px] items-center gap-4 border-b border-border px-4 py-4 last:border-0"
              >
                <div className="flex min-w-0 items-center gap-3">
                  <Skeleton className="h-10 w-10 rounded-lg" />
                  <div className="min-w-0 flex-1 space-y-1.5">
                    <Skeleton className="h-4 w-40" />
                    <Skeleton className="h-3 w-56" />
                  </div>
                </div>
                <Skeleton className="h-5 w-16 rounded-full" />
                <Skeleton className="h-4 w-28" />
                <Skeleton className="h-4 w-20" />
                <Skeleton className="h-8 w-8 rounded-full" />
                <Skeleton className="h-8 w-8 rounded-full" />
              </div>
            ))}
          </div>
        </main>
      </div>
    );
  }

  if (error || !data?.result) {
    return (
      <div className="flex min-h-screen flex-col items-center justify-center gap-3 px-4 text-center">
        <Megaphone className="h-10 w-10 text-muted-foreground/40" />
        <p className="text-base font-medium">
          This Raiseboard isn't available
        </p>
        <p className="max-w-sm text-sm text-muted-foreground">
          The link may be wrong or the workspace owner may have disabled it.
        </p>
      </div>
    );
  }

  // Facet options are read in `filterOptions` above, which has to run before
  // the loading guards, so they aren't destructured here.
  const { opportunity, applications, pagination } = data.result;

  // The board's true size, independent of the current filters — the header
  // should keep saying how big the board is while you narrow it.
  const totalMatching = pagination?.total ?? applications.length;
  const totalPages = pagination?.total_pages ?? 1;
  const hasFilters =
    !!debouncedSearch ||
    stageFilter.length > 0 ||
    industryFilter.length > 0 ||
    countryFilter.length > 0;

  const clearFilters = () => {
    setSearch("");
    setStageFilter([]);
    setIndustryFilter([]);
    setCountryFilter([]);
  };

  return (
    <TooltipProvider delayDuration={200}>
    <div className="h-dvh overflow-y-auto bg-background">
      <div className="border-b border-border bg-card">
        <div className="mx-auto flex h-14 max-w-5xl items-center justify-between gap-4 px-4 sm:px-6">
          <Tooltip>
            <TooltipTrigger asChild>
              <button
                type="button"
                onClick={() => {
                  if (isSignedIn) {
                    navigate(activeWorkspaceId ? `/${activeWorkspaceId}/dashboard` : "/workspaces");
                  } else {
                    navigate("/");
                  }
                }}
                className="flex items-center gap-2 rounded-md transition-opacity hover:opacity-80 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                aria-label="DiligenceGPT"
              >
                <img src={dgptLogo} alt="DiligenceGPT" className="h-7 w-auto" />
              </button>
            </TooltipTrigger>
            <TooltipContent>
              {isSignedIn ? "Open DiligenceGPT" : "Go to DiligenceGPT"}
            </TooltipContent>
          </Tooltip>

          {isLoaded && isSignedIn ? (
            <div className="flex items-center gap-3">
              <span className="hidden truncate text-sm text-muted-foreground sm:inline">
                {user?.firstName
                  ? [user.firstName, user.lastName].filter(Boolean).join(" ")
                  : user?.primaryEmailAddress?.emailAddress ?? ""}
              </span>
              <UserButton
                appearance={{ elements: { avatarBox: "w-8 h-8" } }}
                afterSignOutUrl={typeof window !== "undefined" ? window.location.pathname : "/"}
              />
            </div>
          ) : (
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={() => setAuthReason("Sign in to continue.")}
                className="hidden rounded-md px-3 py-1.5 text-sm font-medium text-muted-foreground transition-colors hover:text-foreground sm:inline-flex"
              >
                Sign in
              </button>
              <Button
                size="sm"
                onClick={() => setAuthReason("Sign in to continue.")}
              >
                Get started
              </Button>
            </div>
          )}
        </div>
      </div>

      <header className="border-b border-border bg-card">
        <div className="mx-auto flex max-w-5xl flex-col gap-2 px-4 py-6 sm:flex-row sm:items-end sm:justify-between sm:px-6 sm:py-8">
          <div>
            <p className="text-[11px] font-semibold uppercase tracking-widest text-muted-foreground">
              Raiseboard
            </p>
            <h1 className="mt-2 font-serif text-3xl text-foreground sm:text-4xl">
              {opportunity?.opportunity_name ?? "Active Deals"}
            </h1>
          </div>
          <div className="flex items-center gap-4">
            <div className="text-sm text-muted-foreground">
              <span className="font-mono tabular-nums">{totalMatching}</span>{" "}
              compan{totalMatching === 1 ? "y" : "ies"}{" "}
              {hasFilters ? "matching" : "actively raising"}
            </div>
            <SubscribeButton slug={slug} onRequestAuth={setAuthReason} />
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-5xl px-4 py-6 sm:px-6 sm:py-8">
        {/* Browse controls. Every one of these is applied server-side — a board
            can hold thousands of deals, so the page never holds them all. */}
        <div className="mb-5 flex flex-col gap-3">
          <div className="relative">
            <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <Input
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              placeholder="Search companies…"
              aria-label="Search companies"
              className="pl-9 pr-9"
            />
            {search && (
              <button
                type="button"
                onClick={() => setSearch("")}
                aria-label="Clear search"
                className="absolute right-2 top-1/2 -translate-y-1/2 rounded-full p-1 text-muted-foreground transition-colors hover:text-foreground"
              >
                <X className="h-4 w-4" />
              </button>
            )}
          </div>

          <div className="flex flex-wrap items-center gap-2">
            <MultiSelect
              id="board-industry"
              options={filterOptions.industries}
              value={industryFilter}
              onValueChange={setIndustryFilter}
              placeholder="Industry"
              triggerIcon={Building2}
              maxCount={1}
              className="w-auto min-w-[150px] rounded-full border-border bg-card"
            />
            <MultiSelect
              id="board-stage"
              options={filterOptions.stages}
              value={stageFilter}
              onValueChange={setStageFilter}
              placeholder="Stage"
              triggerIcon={Layers}
              maxCount={2}
              className="w-auto min-w-[130px] rounded-full border-border bg-card"
            />
            <MultiSelect
              id="board-country"
              options={filterOptions.countries}
              value={countryFilter}
              onValueChange={setCountryFilter}
              placeholder="Location"
              triggerIcon={Globe}
              maxCount={2}
              className="w-auto min-w-[140px] rounded-full border-border bg-card"
            />
            {hasFilters && (
              <Button
                variant="ghost"
                size="sm"
                onClick={clearFilters}
                className="rounded-full text-muted-foreground"
              >
                Clear
              </Button>
            )}
            {isFetching && (
              <Loader2
                className="h-4 w-4 animate-spin text-muted-foreground"
                aria-label="Loading results"
              />
            )}
          </div>
        </div>

        {applications.length === 0 ? (
          <Card>
            <CardContent className="p-8 text-center text-sm text-muted-foreground">
              {hasFilters
                ? "No companies match these filters."
                : "No companies on this board yet — check back soon."}
            </CardContent>
          </Card>
        ) : (
          <>
          {/* Mobile cards */}
          <div className="space-y-3 md:hidden">
            {applications.map((application) => {
              const { company, stageLabel, industry, bio, country } =
                getRowDetails(application);

              return (
                <div
                  key={String(application._id)}
                  className="rounded-lg border border-border bg-card p-4"
                >
                  <div className="flex items-start gap-3">
                    <CompanyAvatar
                      logoUrl={company?.logo_url}
                      name={company?.company_name}
                    />
                    <div className="min-w-0 flex-1">
                      <p className="truncate text-sm font-medium text-foreground">
                        {company?.company_name ?? "Untitled"}
                      </p>
                      {bio && (
                        <p className="line-clamp-2 text-xs text-muted-foreground">
                          {bio}
                        </p>
                      )}
                    </div>
                  </div>
                  <div className="mt-3 flex flex-wrap items-center gap-x-3 gap-y-1.5 text-xs text-muted-foreground">
                    {stageLabel && stageLabel !== "Unknown" && (
                      <Badge
                        variant="outline"
                        className="rounded-sm border-border bg-card text-[10px] font-semibold uppercase tracking-widest text-foreground"
                      >
                        {stageLabel}
                      </Badge>
                    )}
                    {industry && <span className="truncate">{industry}</span>}
                    {country && (
                      <span className="flex items-center gap-1">
                        <MapPin className="h-3 w-3 shrink-0" />
                        {country}
                      </span>
                    )}
                  </div>
                  <div className="mt-3 flex gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      className="flex-1"
                      onClick={() => setViewing(application)}
                    >
                      <Eye className="mr-1.5 h-4 w-4" />
                      View
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      className="flex-1"
                      onClick={() => setEmailing(application)}
                    >
                      <Mail className="mr-1.5 h-4 w-4" />
                      Email
                    </Button>
                  </div>
                </div>
              );
            })}
          </div>

          {/* Desktop table */}
          <div className="hidden overflow-hidden rounded-lg border border-border md:block">
            {/* Table header */}
            <div className="grid grid-cols-[1fr_100px_160px_140px_44px_44px] items-center gap-4 border-b border-border bg-muted/40 px-4 py-3">
              {(["Startup", "Stage", "Industry", "Location"] as const).map(
                (col) => (
                  <span
                    key={col}
                    className="text-[11px] font-semibold uppercase tracking-widest text-muted-foreground"
                  >
                    {col}
                  </span>
                )
              )}
              <span className="col-span-2 text-center text-[11px] font-semibold uppercase tracking-widest text-muted-foreground">
                Actions
              </span>
            </div>

            {/* Rows */}
            {applications.map((application) => {
              const { company, stageLabel, industry, bio, country: rowCountry } =
                getRowDetails(application);

              return (
                <div
                  key={String(application._id)}
                  className="grid grid-cols-[1fr_100px_160px_140px_44px_44px] items-center gap-4 border-b border-border px-4 py-4 last:border-0 hover:bg-muted/20 transition-colors"
                >
                  {/* Avatar + name + bio */}
                  <div className="flex min-w-0 items-center gap-3">
                    <CompanyAvatar
                      logoUrl={company?.logo_url}
                      name={company?.company_name}
                    />
                    <div className="min-w-0">
                      <p className="truncate text-sm font-medium text-foreground">
                        {company?.company_name ?? "Untitled"}
                      </p>
                      {bio && (
                        <p className="truncate text-xs text-muted-foreground">
                          {bio}
                        </p>
                      )}
                    </div>
                  </div>

                  {/* Stage */}
                  <div>
                    {stageLabel && stageLabel !== "Unknown" ? (
                      <Badge
                        variant="outline"
                        className="rounded-sm border-border bg-card text-[10px] font-semibold uppercase tracking-widest text-foreground"
                      >
                        {stageLabel}
                      </Badge>
                    ) : (
                      <span className="font-mono text-xs tabular-nums text-muted-foreground">—</span>
                    )}
                  </div>

                  {/* Industry */}
                  <p className="truncate text-sm text-muted-foreground">
                    {industry ?? "—"}
                  </p>

                  {/* Location */}
                  <div className="flex items-center gap-1 text-sm text-muted-foreground min-w-0">
                    {rowCountry && <MapPin className="h-3 w-3 shrink-0" />}
                    <span className="truncate">{rowCountry ?? "—"}</span>
                  </div>

                  {/* View details */}
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Button
                        size="icon"
                        variant="outline"
                        className="h-8 w-8 shrink-0 rounded-full"
                        onClick={() => setViewing(application)}
                        aria-label="View details"
                      >
                        <Eye className="h-4 w-4" />
                      </Button>
                    </TooltipTrigger>
                    <TooltipContent>View details</TooltipContent>
                  </Tooltip>

                  {/* Send details via email */}
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Button
                        size="icon"
                        variant="outline"
                        className="h-8 w-8 shrink-0 rounded-full"
                        onClick={() => setEmailing(application)}
                        aria-label="Send details via email"
                      >
                        <Mail className="h-4 w-4" />
                      </Button>
                    </TooltipTrigger>
                    <TooltipContent>Send details via email</TooltipContent>
                  </Tooltip>
                </div>
              );
            })}
          </div>

          {totalPages > 1 && (
            <nav
              aria-label="Board pages"
              className="mt-5 flex items-center justify-between gap-4"
            >
              <p className="text-sm text-muted-foreground">
                Showing{" "}
                <span className="font-mono tabular-nums">
                  {(page - 1) * PAGE_SIZE + 1}–
                  {Math.min(page * PAGE_SIZE, totalMatching)}
                </span>{" "}
                of <span className="font-mono tabular-nums">{totalMatching}</span>
              </p>
              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                  disabled={page <= 1}
                >
                  <ChevronLeft className="h-4 w-4" />
                  Previous
                </Button>
                <span className="text-sm text-muted-foreground">
                  Page <span className="font-mono tabular-nums">{page}</span> of{" "}
                  <span className="font-mono tabular-nums">{totalPages}</span>
                </span>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                  disabled={page >= totalPages}
                >
                  Next
                  <ChevronRight className="h-4 w-4" />
                </Button>
              </div>
            </nav>
          )}
          </>
        )}
      </main>

      <DetailsDialog
        application={viewing}
        onClose={() => setViewing(null)}
        onAddToPipeline={(companyId, name) => {
          setViewing(null);
          setPendingAdd({ id: companyId, name });
        }}
        onSendEmail={() => {
          const app = viewing;
          setViewing(null);
          setEmailing(app);
        }}
      />

      <SendEmailDialog
        application={emailing}
        defaultEmail={user?.primaryEmailAddress?.emailAddress ?? ""}
        isSending={sendEmail.isPending}
        onSend={(email) => {
          if (emailing) sendEmail.mutate({ application: emailing, email });
        }}
        onClose={() => setEmailing(null)}
      />

      <AlertDialog
        open={!!pendingAdd}
        onOpenChange={(open) => { if (!open) setPendingAdd(null); }}
      >
        <AlertDialogContent>
          {isSignedIn ? (
            <>
              <AlertDialogHeader>
                <AlertDialogTitle>Add this company to your pipeline?</AlertDialogTitle>
                <AlertDialogDescription>
                  <span className="font-medium">{pendingAdd?.name}</span> will be
                  added as a deal in your DiligenceGPT pipeline, where you can
                  review it and run due diligence.
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel>Cancel</AlertDialogCancel>
                <AlertDialogAction
                  onClick={() => {
                    if (pendingAdd) addToPipeline.mutate(pendingAdd.id);
                    setPendingAdd(null);
                  }}
                >
                  Add to Pipeline
                </AlertDialogAction>
              </AlertDialogFooter>
            </>
          ) : (
            <>
              <AlertDialogHeader>
                <AlertDialogTitle>Add this company to your pipeline</AlertDialogTitle>
                <AlertDialogDescription>
                  Adding <span className="font-medium">{pendingAdd?.name}</span>{" "}
                  to your pipeline requires a DiligenceGPT account. Get started
                  to review this deal and run your own due diligence.
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter className="sm:justify-between">
                <AlertDialogCancel>Cancel</AlertDialogCancel>
                <AlertDialogAction
                  onClick={() => {
                    setPendingAdd(null);
                    setAuthReason("Sign in to add this deal to your pipeline.");
                  }}
                >
                  Get started
                </AlertDialogAction>
              </AlertDialogFooter>
            </>
          )}
        </AlertDialogContent>
      </AlertDialog>

      <BoardAuthDialog
        open={!!authReason}
        onOpenChange={(open) => {
          if (!open) setAuthReason(null);
        }}
        reason={authReason ?? undefined}
      />
    </div>
    </TooltipProvider>
  );
}

function formatRaiseAmount(amount?: string, currency?: string) {
  if (!amount) return null;
  const trimmed = amount.trim();
  if (!trimmed) return null;
  const ccy = currency?.trim();
  return ccy ? `${trimmed} ${ccy}` : trimmed;
}

function toArray(value: string | string[] | undefined): string[] {
  if (!value) return [];
  if (Array.isArray(value)) return value.filter(Boolean);
  return value
    .split(",")
    .map((s) => s.trim())
    .filter(Boolean);
}

function DetailsDialog({
  application,
  onClose,
  onAddToPipeline,
  onSendEmail,
}: {
  application: Application | null;
  onClose: () => void;
  onAddToPipeline: (companyId: string, name: string) => void;
  onSendEmail: () => void;
}) {
  if (!application) return null;
  const company = getCompany(application);
  const companyId =
    typeof application.applicant_company === "string"
      ? application.applicant_company
      : company?._id;

  const name = company?.company_name ?? "Untitled";
  const bio = company?.short_bio ?? application.short_bio;
  const website = company?.company_website ?? application.company_website;
  const linkedin = application.linkedIn;
  const twitter = application.twitter;

  const stageValue = application.stage ?? company?.stage;
  const stageLabel = stageValue != null ? getStageText(stageValue) : null;

  let industry: string | undefined;
  let subIndustry: string | undefined;
  const subIndustriesField = application.deal_info?.find(
    (f) => f.field_name === "sub_industries" && f.field_state === "approved"
  );
  if (subIndustriesField?.field_value) {
    const codes = Array.isArray(subIndustriesField.field_value)
      ? subIndustriesField.field_value
      : [subIndustriesField.field_value as string];
    const firstCode = codes.map(String).find((c) => !!c);
    if (firstCode) {
      subIndustry = GICSSubIndustries.find((si) => si.code === firstCode)?.name;
      const parentCode = firstCode.length >= 8 ? firstCode.substring(0, 8) : undefined;
      if (parentCode) {
        industry = GICSIndustries.find((i) => i.code === parentCode)?.name;
      }
    }
  }
  if (!industry) {
    industry = (application.industry ?? company?.industry)?.[0];
  }

  const dealInfoCountry = application.deal_info?.find(
    (f) => f.field_name === "address"
  )?.field_value as string | undefined;
  const country = dealInfoCountry || company?.country || application.country;

  const raise = formatRaiseAmount(application.attempting_to_raise, application.currency);
  // business_model / revenue_model can live on the application or the company
  // doc; fall back to the company's so the public board doesn't drop them.
  const businessModels = toArray(application.business_model).length
    ? toArray(application.business_model)
    : toArray(company?.business_model);
  const revenueModels = toArray(application.revenue_model).length
    ? toArray(application.revenue_model)
    : toArray(company?.revenue_model);
  const founded = application.year_of_inception;

  const websiteHost = website
    ? website.replace(/^https?:\/\//, "").replace(/\/$/, "")
    : null;
  const websiteHref = website
    ? website.startsWith("http")
      ? website
      : `https://${website}`
    : null;

  const pitchSections: Array<{ label: string; body: string }> = [];
  if (application.objective) pitchSections.push({ label: "Objective", body: application.objective });
  if (application.problem) pitchSections.push({ label: "Problem", body: application.problem });
  if (application.solution) pitchSections.push({ label: "Solution", body: application.solution });
  if (application.traction) pitchSections.push({ label: "Traction", body: application.traction });

  const links: Array<{ label: string; href: string; icon: typeof Linkedin }> = [];
  if (linkedin)
    links.push({
      label: "LinkedIn",
      href: linkedin.startsWith("http") ? linkedin : `https://${linkedin}`,
      icon: Linkedin,
    });
  if (twitter)
    links.push({
      label: "Twitter",
      href: twitter.startsWith("http") ? twitter : `https://${twitter}`,
      icon: Twitter,
    });

  const stats: Array<{ label: string; value: string }> = [];
  if (stageLabel && stageLabel !== "Unknown") stats.push({ label: "Stage", value: stageLabel });
  if (raise) stats.push({ label: "Target raise", value: raise });
  if (country) stats.push({ label: "Country", value: country });
  if (founded) stats.push({ label: "Founded", value: founded });
  if (industry) stats.push({ label: "Industry", value: industry });
  if (subIndustry && subIndustry !== industry) stats.push({ label: "Sub-industry", value: subIndustry });
  if (businessModels.length > 0) stats.push({ label: "Business model", value: businessModels.join(", ") });
  if (revenueModels.length > 0) stats.push({ label: "Revenue model", value: revenueModels.join(", ") });

  return (
    <Dialog open={!!application} onOpenChange={(open) => { if (!open) onClose(); }}>
      <DialogContent className="grid max-h-[88vh] max-w-2xl grid-rows-[1fr_auto] overflow-hidden p-0 gap-0">
        {/* Scrollable body */}
        <div className="min-h-0 overflow-y-auto">
          {/* Header */}
          <div className="flex items-start gap-4 px-6 pt-6 pb-5 pr-12">
            <CompanyAvatar
              logoUrl={company?.logo_url}
              name={company?.company_name}
              size="lg"
            />
            <div className="min-w-0 flex-1">
              <DialogTitle className="font-serif text-2xl leading-snug text-foreground sm:text-3xl">
                {name}
              </DialogTitle>
              {(websiteHost || links.length > 0) && (
                <div className="mt-2 flex flex-wrap items-center gap-x-3 gap-y-1.5">
                  {websiteHost && websiteHref && (
                    <a
                      href={websiteHref}
                      target="_blank"
                      rel="noreferrer"
                      className="inline-flex items-center gap-1 text-sm text-muted-foreground transition-colors hover:text-foreground"
                    >
                      {websiteHost}
                      <ExternalLink className="h-3 w-3" />
                    </a>
                  )}
                  {links.map(({ label, href, icon: Icon }) => (
                    <a
                      key={label}
                      href={href}
                      target="_blank"
                      rel="noreferrer"
                      aria-label={label}
                      className="inline-flex h-6 w-6 items-center justify-center rounded text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
                    >
                      <Icon className="h-3.5 w-3.5" />
                    </a>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Bio */}
          {bio && (
            <div className="px-6 pb-4">
              <p className="text-sm leading-relaxed text-foreground/80">{bio}</p>
            </div>
          )}

          {/* Stats */}
          {stats.length > 0 && (
            <div className="border-t border-border bg-muted/20 px-6 py-5">
              <div className="grid grid-cols-2 gap-x-6 gap-y-4 sm:grid-cols-3">
                {stats.map((s) => {
                  const isNumeric =
                    s.label === "Target raise" || s.label === "Founded";
                  return (
                    <div key={s.label}>
                      <p className="text-[11px] font-semibold uppercase tracking-widest text-muted-foreground">
                        {s.label}
                      </p>
                      <p
                        className={`mt-1 text-sm font-medium text-foreground ${
                          isNumeric ? "font-mono tabular-nums" : ""
                        }`}
                      >
                        {s.value}
                      </p>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Pitch */}
          {pitchSections.length > 0 && (
            <div className="border-t border-border px-6 py-5 space-y-5">
              {pitchSections.map((s) => (
                <section key={s.label}>
                  <h4 className="text-[11px] font-semibold uppercase tracking-widest text-muted-foreground">
                    {s.label}
                  </h4>
                  <p className="mt-1.5 text-sm leading-relaxed text-foreground/85 whitespace-pre-line">
                    {s.body}
                  </p>
                </section>
              ))}
            </div>
          )}

        </div>

        {/* Sticky footer */}
        <div className="flex items-center justify-between gap-2 border-t border-border px-4 py-3 sm:px-6">
          <Button
            variant="outline"
            size="sm"
            onClick={onClose}
            className="hidden sm:inline-flex"
          >
            Close
          </Button>
          <div className="flex w-full items-center gap-2 sm:w-auto">
            <Button
              variant="outline"
              size="sm"
              onClick={onSendEmail}
              className="flex-1 sm:flex-none"
            >
              <Mail className="mr-1.5 h-4 w-4" />
              Send via email
            </Button>
            {companyId && (
              <Button
                size="sm"
                onClick={() => onAddToPipeline(companyId, name)}
                className="flex-1 sm:flex-none"
              >
                <Plus className="mr-1.5 h-4 w-4" />
                Add to Pipeline
              </Button>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function SendEmailDialog({
  application,
  defaultEmail,
  isSending,
  onSend,
  onClose,
}: {
  application: Application | null;
  defaultEmail: string;
  isSending: boolean;
  onSend: (email: string) => void;
  onClose: () => void;
}) {
  const [email, setEmail] = useState(defaultEmail);
  useEffect(() => {
    setEmail(defaultEmail);
  }, [defaultEmail, application]);
  if (!application) return null;
  const company = getCompany(application);
  const name = company?.company_name ?? "this company";
  const valid = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim());
  return (
    <Dialog open={!!application} onOpenChange={(open) => { if (!open) onClose(); }}>
      <DialogContent className="max-w-md">
        <DialogTitle>Email deal details</DialogTitle>
        <p className="text-sm text-muted-foreground">
          Send <span className="font-medium text-foreground">{name}</span>'s details to:
        </p>
        <Input
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder="name@example.com"
          autoFocus
          onKeyDown={(e) => {
            if (e.key === "Enter" && valid && !isSending) onSend(email.trim());
          }}
        />
        <div className="flex justify-end gap-2">
          <Button variant="outline" size="sm" onClick={onClose} disabled={isSending}>
            Cancel
          </Button>
          <Button size="sm" onClick={() => onSend(email.trim())} disabled={!valid || isSending}>
            {isSending ? (
              <Loader2 className="mr-1.5 h-4 w-4 animate-spin" />
            ) : (
              <Mail className="mr-1.5 h-4 w-4" />
            )}
            Send
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
